import {
  OUTBOUND_CALL_OUTCOME_LABELS,
  campaignPlacesCalls,
  checkedAudience,
  describeCampaign,
  describeStepAction,
  describeTriggerPhrase,
  describeWindow,
  plural,
  readinessNeeds,
  stepName,
  type CampaignSentence,
  type CampaignSpec,
  type DescribeOptions,
  type SequenceStep,
} from '@vagusx/shared';

/**
 * The Setup tab's read of a campaign: the same facts `describeCampaign` states,
 * laid out to be scanned rather than read.
 *
 * `describeCampaign` writes one sentence per editor control, because the text
 * panel anchors clicks to them and the compiler reads that text back. Read top
 * to bottom on a read-only page, the same sentences repeat once per step — four
 * steps that share their timing, callback and carry-on rule print each of them
 * four times, and the few facts that differ get lost between them.
 *
 * So this says a rule ONCE when every step holds it, and keeps it on the step
 * otherwise. Nothing is dropped: a step's own sentences come from
 * `describeCampaign` itself, grouped rather than rewritten, and its row is the
 * two phrases its main sentence is made of, so a setting that module learns to
 * describe reaches this page without a change here.
 */
export interface CampaignSummary {
  facts: { hours: string; limit: string; expiry: string };
  audience: {
    /** `Calls open referrals.` and one sentence per audience setting. */
    sentences: CampaignSentence[];
    /** What a patient needs on file before they are called; empty when nothing is checked. */
    requires: string[];
  };
  sequence: {
    rows: StepRow[];
    /** Sentences every step holds, said once. */
    everyStep: string[];
    /**
     * The outcomes that move every step on to the next, when every step but the
     * last agrees. Null when they differ (each row then carries its own) or when
     * there is only one step.
     */
    carriesOnOn: string[] | null;
    lastStepName: string;
  };
  ending: {
    success: string;
    /** Outcomes that stop the run at any step. */
    stopsOn: string[];
  };
}

export interface StepRow {
  id: string;
  name: string;
  /** `as soon as they qualify`, `1 day later` — sentence-medial, as `describeCampaign` writes it. */
  when: string;
  /** `calls twice, 5 hours apart, leaving a message if a machine answers`. */
  action: string;
  /** Sentences true of this step and not of every step. */
  details: string[];
  /**
   * Its own carry-on outcomes, only when the steps disagree. An empty array is
   * a step that always ends the run; null means the shared rule (or the last step).
   */
  carriesOnOn: string[] | null;
}

/** A step's own sentence ids are `step.<id>.<suffix>`; these are drawn by the layout itself. */
const DRAWN_BY_LAYOUT = new Set(['continue', 'end']);

/** `Not answered`, `Voicemail` — the picker's chip labels, which is what these render as. */
export function outcomeLabels(values: readonly string[]): string[] {
  // Picker order, not authoring order, so two steps holding the same set read the same.
  const known = OUTBOUND_CALL_OUTCOME_LABELS.filter((o) => values.includes(o.value)).map(
    (o) => o.label
  );
  // An outcome this build has no label for is a spec from a newer one: shown raw, never dropped.
  const unknown = values.filter((v) => !OUTBOUND_CALL_OUTCOME_LABELS.some((o) => o.value === v));
  // The arrays are plain strings with no uniqueness; a repeat means nothing more.
  return [...new Set([...known, ...unknown])];
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  const left = new Set(a);
  const right = new Set(b);
  return left.size === right.size && [...left].every((v) => right.has(v));
}

/** What a patient needs on file before they are called, as checked (ADR-333). */
function describeRequires(spec: CampaignSpec): string[] {
  const audience = checkedAudience(spec);
  if (audience.type === 'open_referral') return readinessNeeds(audience.readiness);
  return campaignPlacesCalls(spec) ? ['a date of birth on file'] : [];
}

/**
 * Each step's own sentences, keyed by suffix (`timing`, `callback`, …).
 *
 * A sentence belongs to the step whose id is the LONGEST that prefixes it, and
 * a step's main sentence (`step.<id>`, no suffix) belongs to no other step:
 * ids are free strings, so with steps `a` and `a.b` the id `step.a.b` is
 * `a.b`'s main sentence, not a sentence `b` of step `a`.
 */
function stepSentences(
  sequence: readonly SequenceStep[],
  sentences: readonly CampaignSentence[]
): Map<string, Map<string, string>> {
  const owned = new Map(sequence.map((step) => [step.id, new Map<string, string>()]));
  const mains = new Set(sequence.map((step) => `step.${step.id}`));
  const longestFirst = [...sequence].sort((a, b) => b.id.length - a.id.length);

  for (const sentence of sentences) {
    if (mains.has(sentence.id)) continue;
    const owner = longestFirst.find((step) => sentence.id.startsWith(`step.${step.id}.`));
    if (!owner) continue;
    const suffix = sentence.id.slice(`step.${owner.id}.`.length);
    if (!DRAWN_BY_LAYOUT.has(suffix)) owned.get(owner.id)!.set(suffix, sentence.text);
  }
  return owned;
}

export function summarizeCampaign(
  spec: CampaignSpec,
  options: Pick<DescribeOptions, 'names'> = {}
): CampaignSummary {
  // As checked, like the rest of this page: a campaign that calls needs a date
  // of birth whatever its switch says (`DescribeOptions.asChecked`).
  const sections = describeCampaign(spec, { ...options, asChecked: true });
  const sentencesOf = (id: string): CampaignSentence[] =>
    sections.find((s) => s.id === id)?.sentences ?? [];

  const { sequence } = spec;
  const owned = stepSentences(sequence, sentencesOf('sequence'));
  const perStep = sequence.map((step) => owned.get(step.id)!);

  // A sentence is said once when every step holds it word for word. Not for a
  // single step: "every step" over one step is a heading with nothing to share.
  const everyStep: string[] = [];
  const shared = new Set<string>();
  if (sequence.length > 1) {
    for (const [suffix, text] of perStep[0]!) {
      if (perStep.every((own) => own.get(suffix) === text)) {
        shared.add(suffix);
        everyStep.push(text);
      }
    }
  }

  // Every step but the last carries on to the next; the last ends the run.
  const carrying = sequence.slice(0, -1);
  const sharedCarry =
    carrying.length > 0 &&
    carrying[0]!.continueOn.length > 0 &&
    carrying.every((step) => sameSet(step.continueOn, carrying[0]!.continueOn))
      ? outcomeLabels(carrying[0]!.continueOn)
      : null;

  const rows = sequence.map((step, index): StepRow => {
    const isLast = index === sequence.length - 1;
    const details = [...perStep[index]!]
      .filter(([suffix]) => !shared.has(suffix))
      .map(([, text]) => text);
    return {
      id: step.id,
      name: stepName(step, index),
      when: describeTriggerPhrase(step),
      action: describeStepAction(step),
      details,
      carriesOnOn: isLast || sharedCarry ? null : outcomeLabels(step.continueOn),
    };
  });

  return {
    facts: {
      hours: describeWindow(spec.window),
      // Every attempt counts toward the cap, whichever channel it is on.
      limit: `At most ${plural(spec.maxTotalAttempts, 'attempt')}`,
      expiry:
        spec.expiresAfterDays == null
          ? 'No time limit'
          : `Gives up after ${plural(spec.expiresAfterDays, 'day')}`,
    },
    audience: {
      sentences: sentencesOf('audience').filter((s) => s.id !== 'audience.readiness'),
      requires: describeRequires(spec),
    },
    sequence: {
      rows,
      everyStep,
      carriesOnOn: sharedCarry,
      lastStepName: rows[rows.length - 1]!.name,
    },
    ending: {
      success: sentencesOf('ending').find((s) => s.id === 'ending.success')?.text ?? '',
      stopsOn: outcomeLabels(spec.stopOn),
    },
  };
}
